import classnames from 'classnames/bind'
import Head from 'next/head'
import { useRouter } from 'next/router'
import React, { useEffect, useRef, useState } from 'react'

import {
  useIsHover,
  useIsomorphicLayoutEffect,
} from '@unlikelystudio/react-hooks'

import Burger from '~/components/Burger'
import Image from '~/components/Image'
import ImageWithPlaceHolder from '~/components/ImageWithPlaceholder'
import InlineCta from '~/components/InlineCta'
import LangSwitcher from '~/components/LangSwitcher'
import Link from '~/components/Link'
import { LinkProps } from '~/components/Link'
import RichText, { RichTextBlock } from '~/components/RichText'
import RoundedCta from '~/components/RoundedCta'

import { useGlobalData } from '~/providers/GlobalDataProvider'
import { useStyle } from '~/providers/StyleProvider'

import useClickOutside from '~/hooks/useClickOutside'
import useOnRouterChange from '~/hooks/useOnRouterChange'

import { isCurrentRoute } from '~/utils/is-current-route'

import Dropdown from '../Dropdown'
import { ReactComponent as DropdownIcon } from '../dropdown.svg'
import { NavigationProps } from '../index'
import css from './styles.module.scss'

const cx = classnames.bind(css)

export interface NavigationDesktopProps extends NavigationProps {
  className?: string
  isVisible?: boolean
}
/**
 * Navigation desktop component
 * @param props
 * @returns Component
 */

export interface NavigationItemProps {
  title?: RichTextBlock[]
  navigationItem?: LinkProps[]
}
export interface NavItemLinkProps {
  className?: string
  item?: NavigationItemProps
  index?: string | number
  isVisible?: boolean
}

function NavItemLink({ className, item, index, isVisible }: NavItemLinkProps) {
  const itemLinkStyle = useStyle({ textPreset: 'cta-small' })

  const [isOpen, setIsOpen] = useState(false)
  const navItemRef = useRef(null)
  const dropdownContainerRef = useRef(null)

  useClickOutside([navItemRef, dropdownContainerRef], () => setIsOpen(false))
  useOnRouterChange(() => setIsOpen(false))

  const { title, navigationItem } = item

  const handleClick = () => {
    setIsOpen(!isOpen)
  }

  useEffect(() => {
    if (!isVisible) setIsOpen(false)
  }, [isVisible])

  return (
    <div>
      <div
        className={cx(css.link, { active: isOpen })}
        onClick={handleClick}
        ref={navItemRef}
        aria-haspopup={true}
        aria-expanded={false}>
        {title &&
          (Array.isArray(title) ? (
            <RichText
              className={cx(itemLinkStyle, css.linkText)}
              render={title}
            />
          ) : (
            <div className={cx(itemLinkStyle, css.linkText)}>{title}</div>
          ))}
        <DropdownIcon className={css.dropdownIcon} width={10} height={10} />
      </div>

      {navigationItem?.length > 0 && (
        <div
          ref={dropdownContainerRef}
          className={cx(css.linkDropDown, className, {
            isOpen: isOpen,
          })}>
          <Dropdown isOpen={isOpen} items={navigationItem} />
        </div>
      )}
    </div>
  )
}

function NormalLink({ item }) {
  const itemLinkStyle = useStyle({ textPreset: 'cta-small' })

  const router = useRouter()

  return (
    <div className={cx(css.link)}>
      <InlineCta
        textPreset="cta-small"
        className={itemLinkStyle}
        {...item}
        theme={isCurrentRoute(item?.href) ? 'primary' : 'inherit'}
        forceUnderline={isCurrentRoute(item?.href) ? true : false}
        lineProps={{
          initialLineState: isCurrentRoute(item?.href) ? 'underlined' : 'none',
          thickness: 2,
        }}
        type="bottom_center"
      />
    </div>
  )
}
function NavigationDesktop({
  className,
  logo,
  items,
  login,
  register,
  isVisible = true,
  alternateLangs,
  ariaHidden = false,
}: NavigationDesktopProps) {
  const itemLinkStyle = useStyle({ textPreset: 'cta-small' })
  const { redirects } = useGlobalData()

  const dropdownContainerRef = useRef(null)
  const burgerContainerRef = useRef(null)

  const [openedDropdownBurger, setOpenedDropDownBurger] = useState(false)

  useClickOutside([dropdownContainerRef, burgerContainerRef], () =>
    setOpenedDropDownBurger(false),
  )

  useOnRouterChange(() => setOpenedDropDownBurger(false))

  useEffect(() => {
    if (!isVisible) setOpenedDropDownBurger(false)
  }, [isVisible])

  return (
    <div
      className={cx(className, css.NavigationDesktop)}
      aria-hidden={ariaHidden}>
      <div className={cx(css.content)}>
        {logo && (
          <Link
            {...redirects?.homepage}
            className={css.logoContainer}
            title={logo?.alt}>
            <h1>
              <ImageWithPlaceHolder
                className={cx(css.logo)}
                layout="fill"
                objectFit="contain"
                loading="eager"
                {...logo}
              />
            </h1>
          </Link>
        )}
        <nav className={css.links}>
          {items?.map((item, index) => {
            if (!item) return null
            if ('navigationItem' in item)
              return (
                <NavItemLink
                  item={item}
                  key={`navigation-item${index}`}
                  index={index}
                  isVisible={isVisible}
                />
              )
            else
              return <NormalLink item={item} key={`navigation-item${index}`} />
          })}
        </nav>
        <nav className={css.right}>
          <div ref={burgerContainerRef}>
            <Burger
              className={cx(css.burger, css.hideOnLargeScreen)}
              isOpen={openedDropdownBurger}
              onClick={() =>
                setTimeout(
                  () => setOpenedDropDownBurger(!openedDropdownBurger),
                  0,
                )
              }
            />
          </div>

          <div className={cx(css.rightContent, css.hideOnSmallScreen)}>
            <LangSwitcher
              className={css.langSwitcher}
              alternateLangs={alternateLangs}
            />
            <div className={css.loginContainer}>
              {login && 'navigationItem' in login ? (
                <NavItemLink
                  item={login}
                  index={'login'}
                  isVisible={isVisible}
                />
              ) : (
                login && <NormalLink item={login} />
              )}
            </div>
            <RoundedCta theme="primary" {...register} />
          </div>

          <div ref={dropdownContainerRef}>
            <Dropdown
              className={cx(css.dropdown, {
                isOpen: openedDropdownBurger && isVisible,
              })}
              register={register}
              login={login}
              isOpen={openedDropdownBurger && isVisible}
              alternateLangs={alternateLangs}
            />
          </div>
        </nav>
      </div>
    </div>
  )
}

NavigationDesktop.defaultProps = {}

export default NavigationDesktop
